import { Fragment, ReactNode } from "react";

import ShowSidebarButton from "@apps/main/components/show-sidebar-button";
import Button from "@components/button/button";
import { ButtonSize, buttonTheme } from "@components/button/button-constants";
import { TopicflowIcon } from "@components/topicflow-icon/topicflow-icon";

export type SettingsPageBreadcrumb = {
  title: string;
  // The last breadcrumb is the current page and is never a link.
  url?: string;
};

// Sticky page header for org settings pages: breadcrumbs on the left, page
// actions (e.g. save/discard) on the right. The negative margins cancel the
// settings container padding so the header spans the full content width;
// pair it with MainContainer's `hideFloatingHeader` so they don't overlap.
// On phones the actions wrap onto a second row rather than sitting on top of
// the breadcrumbs and tabs.
const SettingsPageHeader = ({
  breadcrumbs,
  afterBreadcrumbs,
  children,
}: {
  breadcrumbs: SettingsPageBreadcrumb[];
  // Tabs for the pages this one sits among, beside the trail that names it.
  afterBreadcrumbs?: ReactNode;
  children?: ReactNode;
}) => (
  <div
    className="sticky top-0 z-sticky -mx-6 md:-mx-16 -mt-12 mb-8 min-h-14 md:h-14 py-2 md:py-0 px-6 md:px-16 border-b bg-content-background flex flex-wrap md:flex-nowrap items-center justify-between gap-x-4 gap-y-2 tf-print-display-none"
    aria-label="Settings page header"
  >
    <div className="flex items-center gap-2 min-w-0 flex-1">
      <ShowSidebarButton />
      <nav
        aria-label="Breadcrumb"
        className="flex items-center gap-2 min-w-0 text-sm text-gray-600"
      >
        {breadcrumbs.map((breadcrumb, index) => {
          const isLast = index === breadcrumbs.length - 1;
          return (
            <Fragment key={`${index}-${breadcrumb.title}`}>
              {isLast || !breadcrumb.url ? (
                <span
                  className="font-medium text-gray-800 truncate"
                  aria-current={isLast ? "page" : undefined}
                >
                  {breadcrumb.title}
                </span>
              ) : (
                <Button
                  theme={buttonTheme.text}
                  size={ButtonSize.medium}
                  to={breadcrumb.url}
                  className="shrink-0"
                >
                  {breadcrumb.title}
                </Button>
              )}
              {!isLast && (
                <TopicflowIcon
                  type="chevronRight"
                  className="h-3 w-3 text-gray-500 shrink-0"
                />
              )}
            </Fragment>
          );
        })}
      </nav>
      {afterBreadcrumbs}
    </div>
    {children && (
      <div className="flex items-center gap-2 shrink-0 ml-auto">{children}</div>
    )}
  </div>
);

export default SettingsPageHeader;
